import { useCallback, useEffect, useState } from 'react';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

/**
 * Hook for reCAPTCHA v3 token generation
 */
export const useRecaptcha = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // If key is not configured, don't try to load script
    if (!RECAPTCHA_SITE_KEY) return;

    // Check if already loaded
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => setIsReady(true));
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => {
      window.grecaptcha.ready(() => setIsReady(true));
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  const executeRecaptcha = useCallback(async (action: string): Promise<string> => {
    if (!RECAPTCHA_SITE_KEY) {
      // Development mode - return dummy token
      console.warn('reCAPTCHA site key not configured, using development stub');
      return 'development-token';
    }

    if (!isReady || !window.grecaptcha) {
      // If we're here and we have a key but script failed/wasn't ready,
      // we might want to throw or return a dummy if in dev.
      // But for safety let's throw if it's supposed to be working
      console.warn('reCAPTCHA not ready yet');
      // Attempt generic token or throw
      if (import.meta.env.DEV) return 'development-token';
      throw new Error('reCAPTCHA not ready');
    }

    return window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  }, [isReady]);

  return {
    isReady,
    executeRecaptcha,
  };
};
